import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormGroup } from 'reactstrap';
import * as yup from 'yup';
import { N_YEARS } from '../../constants/years';
import {
  DeleteFarmCropLossEventMutation,
  DeleteTemplateLossEventMutation,
  UpsertCropTemplateMutation,
  UpsertFarmCropMutation,
} from '../../mutations';
import { asCadence, asPercentage } from '../../utils/formatters';
import { useRecord } from '../../utils/hooks';
import { objectWithFields } from '../../utils/utils';
import { nullableNumber } from '../../utils/yup';
import { searchBy } from '../../utils/search';
import { ShrinkContent } from '../ShrinkContent';
import DropdownButton from '../basics/DropdownButton';
import LineItemsTable from '../tables/LineItemsTable';
import ModalForm from './ModalForm';
import { NameInput, ValidatedInput } from './template-components';
import useCropExportFileName from '../../hooks/useCropExportFileName';

const lossTypeOptions = {
  percentLoss: { name: 'Crop Loss', value: 'percentLoss' },
  percentTreeLoss: { name: 'Tree Loss', value: 'percentTreeLoss' },
};

const COLS = [
  {
    accessor: 'name',
    displayString: 'Name',
  },
  {
    accessor: 'startingYear',
    displayString: 'Starting Year',
  },
  {
    accessor: 'endingYear',
    displayString: 'Ending Year',
  },
  {
    accessor: 'cadence',
    displayString: 'Cadence',
    format: asCadence,
  },
  {
    accessor: 'displayLossPercent',
    displayString: 'Percent Loss',
    format: asPercentage,
  },
  {
    accessor: 'displayLossType',
    displayString: 'Loss Type',
  },
];

const fields = [
  'name',
  'startingYear',
  'endingYear',
  'cadence',
  'percentLoss',
  'percentTreeLoss',
];

const queryString = `
  id
  name
  updatedAt
  lossEvents {
    id
    ${fields.join(' ')}
  }
`;

const TemplateLossEventsQuery = gql`
  query TemplateLossEvents($id: ID!) {
    cropTemplate(id: $id) {
      ${queryString}
      team {
        id
      }
    }
  }
`;

const RawCropLossEventsQuery = gql`
  query RawCropLossEvents($id: ID!) {
    rawCrop(id: $id) {
      ${queryString}
    }
  }
`;

const defaultLossEvent = objectWithFields(fields);

const validateLossType = (otherLossType) =>
  yup
    .number()
    .nullable()
    .when(otherLossType, {
      is: null,
      then: (schema) =>
        schema
          .positive('Must be positive')
          .max(100, 'Must be 100 or less')
          .required('Required'),
    });

const lossEventSchema = yup.object().shape(
  {
    name: yup.string().required('Required'),
    startingYear: yup
      .number()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .max(N_YEARS, `Must be ${N_YEARS} or less`)
      .required('Required'),
    endingYear: nullableNumber()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .min(yup.ref('startingYear'), "Ending year can't be before starting year")
      .max(N_YEARS, `Must be ${N_YEARS} or less`),
    cadence: yup
      .number()
      .positive('Must be positive')
      .integer('Must be a whole number')
      .required('Required'),
    // Exactly one of percentLoss and percentTreeLoss must be null
    percentLoss: validateLossType('percentTreeLoss'),
    percentTreeLoss: validateLossType('percentLoss'),
  },
  // Eliminates circular dependency of these fields
  [['percentLoss', 'percentTreeLoss']],
);

const transformValues = (values, { id, updatedAt, team }) => ({
  id,
  updatedAt,
  teamId: team?.id,
  lossEvents: [
    {
      ...values,
      __typename: undefined,
    },
  ],
});

const CropLossEvents = ({
  onSubmit,
  onDeleteWithVars,
  resetMutationState,
  msg,
  resource,
  status,
}) => {
  const [editing, setEditing] = useState(null);
  // lossType is a derived value based on which loss field
  // (percentLoss or percentTreeLoss) is defined on the loss event.
  const [lossType, setLossType] = useState(lossTypeOptions.percentLoss);
  const csvFileName = useCropExportFileName({
    cropName: resource?.name,
    title: 'Loss Event',
  });

  const { loading } = status;

  const getItems = (itemData) => {
    const { lossEvents } = itemData;
    return (
      lossEvents?.map((item) => {
        const itemLossType = item.percentTreeLoss
          ? lossTypeOptions.percentTreeLoss
          : lossTypeOptions.percentLoss;
        return {
          ...item,
          displayLossPercent: item[itemLossType.value],
          displayLossType: itemLossType.name,
          onClick: () => {
            resetMutationState();
            setEditing(item);
            setLossType(itemLossType);
          },
        };
      }) || []
    );
  };

  return (
    <>
      <ShrinkContent>
        <LineItemsTable
          data={resource}
          msg={msg}
          cols={COLS}
          csvButtonInToolbar
          csvFileName={csvFileName}
          getItems={getItems}
          createButton="New Loss Event"
          createButtonOnClick={() => {
            resetMutationState();
            setEditing(defaultLossEvent);
          }}
          search={searchBy(['name'])}
        />
      </ShrinkContent>
      <ModalForm
        editing={editing}
        onClose={() => setEditing(null)}
        loading={loading}
        validationSchema={lossEventSchema}
        onSubmit={onSubmit(lossEventSchema)}
        onDelete={onDeleteWithVars({ lossEventId: editing?.id })}
      >
        {({ setValues, values }) => (
          <>
            <FormGroup>
              <NameInput name="name" placeholder="Loss event name" />
            </FormGroup>
            <div className="d-flex">
              <ValidatedInput
                type="number"
                name="startingYear"
                label="Starting Year"
                placeholder="Start"
                min={0}
                max={30}
              />
              <ValidatedInput
                type="number"
                name="endingYear"
                label="Ending Year"
                placeholder="End"
                min={values.startingYear || 0}
                max={30}
              />
              <ValidatedInput
                type="number"
                name="cadence"
                label="Cadence (years)"
                placeholder="Cadence"
                min={0}
                max={30}
              />
            </div>
            <ValidatedInput
              type="number"
              name={lossType.value}
              label={`% ${lossType.name} Per Year`}
              placeholder="Loss"
              min={0}
              max={100}
              step="any"
              unit="%"
            >
              {({ renderLabel, renderError, renderInput }) => (
                <FormGroup>
                  {renderLabel()}
                  <div className="d-flex">
                    {renderInput()}
                    <DropdownButton
                      inModal
                      outline
                      size="sm"
                      className="ml-2"
                      options={Object.values(lossTypeOptions)}
                      selectedOption={lossType.name}
                      setOption={(option) => {
                        const { value: newLossType } = option;
                        const { value: oldLossType } = lossType;
                        if (oldLossType === newLossType) return;
                        setValues({
                          ...values,
                          [newLossType]: values[oldLossType],
                          [oldLossType]: null,
                        });
                        setLossType(option);
                      }}
                    />
                  </div>
                  {renderError()}
                </FormGroup>
              )}
            </ValidatedInput>
          </>
        )}
      </ModalForm>
    </>
  );
};

export const TemplateCropLossEvents = () => {
  const props = useRecord({
    submitMutation: UpsertCropTemplateMutation,
    deleteMutation: DeleteTemplateLossEventMutation,
    fetchRecordQuery: TemplateLossEventsQuery,
    onDeleteRefetchQuery: TemplateLossEventsQuery,
    transformValues,
  });
  return <CropLossEvents {...props} />;
};

export const FarmCropLossEvents = () => {
  const props = useRecord({
    submitMutation: UpsertFarmCropMutation,
    deleteMutation: DeleteFarmCropLossEventMutation,
    fetchRecordQuery: RawCropLossEventsQuery,
    onDeleteRefetchQuery: RawCropLossEventsQuery,
    transformValues,
    includeFarmId: true,
  });
  return <CropLossEvents {...props} />;
};
